import { css, keyframes } from '@emotion/react';
import { Box, Text } from '@inflearn/ds-react-next';
import type { ParseKeys } from 'i18next';
import { map } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useState } from 'react';

const MESSAGES = [
  '인프런에서 다양한 학습의 기회를 얻으세요',
  '랠릿에서 학습한 역량을 펼쳐보세요',
  '나의 온라인 사수, 인프런',
  '나의 커리어 메이트, 랠릿',
  '인프런과 랠릿에서 가치를 높이고 성장해 보세요',
] as const;

const DURATION = 3000;

export const TextSlider = () => {
  const { t } = useTranslation();
  const [isMounted, setIsMounted] = useState(false);
  const [executeAnimation, setExecuteAnimation] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [messages, setMessages] = useState<[ParseKeys, ParseKeys]>([MESSAGES[0], MESSAGES[0]]);

  /**
   * 애니메이션을 재시작하는 함수 (infinite 사용하지 않은 이유: 깜빡임 현상 방지)
   */
  const restartAnimation = useCallback(() => {
    setExecuteAnimation(false);
    setTimeout(() => {
      setExecuteAnimation(true);
    }, 100);
  }, []);

  const changeMessage = useCallback(() => {
    const nextIndex = currentIndex + 1;
    setCurrentIndex(nextIndex);

    /**
     * 이전 메시지 제거, 다음 메세지 추가
     */
    setMessages((prev: ParseKeys[]) => [prev[1], MESSAGES[nextIndex % MESSAGES.length]]);
    restartAnimation();
  }, [currentIndex, restartAnimation]);

  useEffect(() => {
    if (!isMounted) {
      setIsMounted(true);

      return;
    }

    setTimeout(() => {
      changeMessage();
    }, DURATION);
  }, [isMounted, currentIndex, changeMessage]);

  return (
    <Box css={styleScrollingText}>
      <Box w="100%" css={executeAnimation && styleTextContainer}>
        {map(messages, (message) => (
          <Text
            key={message}
            component="span"
            ta="center"
            styles={{
              root: styleText,
            }}
          >
            {t(message)}
          </Text>
        ))}
      </Box>
    </Box>
  );
};

const styleScrollingText = css({
  width: '100%',
  overflow: 'hidden',
  display: 'flex',
  whiteSpace: 'nowrap',
});
const styleText = css({
  display: 'inline-block',
  width: '100%',
});

const keyframesMarquee = keyframes({
  '0%': { transform: 'translateX(0)' },
  '80%': { transform: 'translateX(-100%)' },
  '100%': { transform: 'translateX(-100%)' },
});

const styleTextContainer = css({
  animation: `${keyframesMarquee} ${DURATION}ms ease-in-out`,
});
